import { useEffect, useState } from 'react';

import { groupBy } from 'lodash';

import { DealMediaTypeEnum, Maybe } from '../../../../../gql/generated/graphql';

import {
  DEAL_MEDIA_RELEASE_OF_INTEREST_TYPES,
  getDealMediaTypeLabel,
} from '../../../../../constants/media';
import { DealMedia } from '../../../../../types/media';
import { parseFilename } from '../../../utils';

export interface MediaCenterSorting {
  order: 'ASC' | 'DESC';
  value: 'category' | 'file_name';
}

export type MediaTableItem = DealMedia & {
  fileExtension: string;
  filePath: string;
  friendlyName: string;
  group: string;
  isPdf: boolean;
  isUploaded: boolean;
  sourceFileName: string;
};

const DMT = DealMediaTypeEnum; // Abbreviation to improve readability

export const groupedMappings: Partial<Record<DealMediaTypeEnum, DealMediaTypeEnum[]>> = {
  [DMT.PowerOfAttorney]: [
    DMT.StateSpecificPowerOfAttorney,
    DMT.SecurePowerOfAttorney,
    DMT.RegistrationSpecificPowerOfAttorney,
    DMT.PowerOfAttorneyCobuyer,
    DMT.StateSpecificPowerOfAttorneyCobuyer,
    DMT.CobuyerSecurePowerOfAttorney,
    DMT.CobuyerRegistrationSpecificPowerOfAttorney,
    DMT.StateAndLienholderSpecificPowerOfAttorney,
    DMT.ProcessorPowerOfAttorney,
    DMT.CobuyerProcessorPowerOfAttorney,
    DMT.MontrosePowerOfAttorney,
  ],
  [DMT.OdoDoc]: [
    DMT.LeaseendOdoDoc,
    DMT.LeaseendOdoDocCobuyer,
    DMT.LeaseendOdoDocJoint,
    DMT.StateSpecificOdoDoc,
    DMT.CobuyerStateSpecificOdoDoc,
    DMT.HondaSpecificOdometerDoc,
    DMT.AcuraSpecificOdometerDoc,
    DMT.FirstFedOdometerDisclosure,
  ],
  [DMT.StateSpecificDocs]: [
    DMT.ColoradoSupplementalIdentificationClause,
    DMT.ColoradoCobuyerSupplementalIdentificationClause,
    DMT.ColoradoJointTenancy,
    DMT.ConnecticutElectronicRegistrationAndTitleAgreement,
    DMT.CoBuyerConnecticutElectronicRegistrationAndTitleAgreement,
    DMT.IllinoisLeasedVehicleRegistrationAffirmation,
    DMT.AlabamaAirbagDisclosureStatement,
    DMT.NewHampshireRegistrationCopyApplication,
    DMT.CaliforniaSpecialInterestLicensePlateApplication,
    DMT.NewYorkCpsNoticeOfLien,
  ],
  [DMT.StateSpecificDamageDisclosure]: [DMT.LouisianaMercedesDamageDisclosure],
  [DMT.ProofOfResidence]: [DMT.ProofOfResidence2],
  [DMT.PurchaseOrder]: [DMT.StateSpecificPurchaseOrder],
  [DMT.TitleApplication]: [DMT.CaliforniaStatementOfFacts, DMT.CaliforniaStatementOfFactsCobuyer],
  [DMT.ElectronicSignatureVerification]: [
    DMT.ElectronicSignatureAttestation,
    DMT.ElectronicSignatureCertification,
  ],
  [DMT.ReleaseOfInterest]: DEAL_MEDIA_RELEASE_OF_INTEREST_TYPES,
  [DMT.SignedDocs]: [DMT.PayoffPacket],
  [DMT.VscAndGap]: [DMT.AllyCertifiedPreOwnedAcknowledgement],
};

const replaceMap = new Map(
  Object.entries(groupedMappings).flatMap(([targetType, sourceTypes]) =>
    sourceTypes.map((sourceType) => [sourceType, targetType as DealMediaTypeEnum]),
  ),
);

const getMediaCategory = (type: Maybe<DealMediaTypeEnum>) => {
  if (!type) {
    return undefined;
  }
  return replaceMap.get(type) || type;
};

const mapGroupMedia = (media: DealMedia): MediaTableItem => {
  const { sourceFileName, fileExtension, isPdf, friendlyName } = parseFilename(media.key ?? '');
  const isUploaded = !!media.id;
  const filePath = decodeURIComponent(media.key ?? '');

  return {
    ...media,
    group: getDealMediaTypeLabel(getMediaCategory(media.type)) || 'Uncategorized',
    filePath,
    isUploaded,
    sourceFileName,
    fileExtension,
    isPdf,
    friendlyName,
  };
};

const sortMediaRowsFactory = (sort: MediaCenterSorting) => (a: DealMedia, b: DealMedia) => {
  if (sort.value === 'category') {
    if (!a.type) {
      return 1;
    }

    if (!b.type) {
      return -1;
    }

    return sort.order === 'DESC'
      ? getDealMediaTypeLabel(getMediaCategory(a.type)) >
        getDealMediaTypeLabel(getMediaCategory(b.type))
        ? 1
        : -1
      : getDealMediaTypeLabel(getMediaCategory(a.type)) >
        getDealMediaTypeLabel(getMediaCategory(b.type))
      ? -1
      : 1;
  }
  const { sourceFileName: aKey } = parseFilename(a.key ?? '');
  const { sourceFileName: bKey } = parseFilename(b.key ?? '');
  return sort.order === 'DESC' ? (aKey > bKey ? 1 : -1) : aKey < bKey ? 1 : -1;
};

export const useMediaCenterRows = (rawMedia: DealMedia[]) => {
  const [media, setMedia] = useState(
    groupBy(
      rawMedia.map((x) => mapGroupMedia(x)),
      (x) => x.group,
    ),
  );
  const [sorting, setSorting] = useState<MediaCenterSorting>({
    order: 'DESC',
    value: 'category',
  });
  const [hiddenGroups, setHiddenGroups] = useState<string[]>([]);

  const onSortChange = (value: MediaCenterSorting['value']) => {
    const isCurrentValue = sorting.value === value;
    setSorting({
      order: isCurrentValue ? (sorting.order === 'ASC' ? 'DESC' : 'ASC') : 'DESC',
      value,
    });
  };

  useEffect(() => {
    const sortFn = sortMediaRowsFactory(sorting);
    setMedia(groupBy(rawMedia.map((x) => mapGroupMedia(x)).sort(sortFn), (x) => x.group));
  }, [rawMedia, sorting]);

  return { hiddenGroups, setHiddenGroups, media, onSortChange, setMedia, sorting, setSorting };
};
